import { useRef, type ChangeEvent, type ReactNode } from 'react'
import { Button } from './ui.tsx'

/**
 * Botón que abre el selector de fotos y devuelve el archivo elegido.
 *
 * Cada botón lleva su propio input asociado, en vez de compartir uno con un
 * estado que recuerde a qué tarea corresponde: ese estado se puede perder entre
 * el toque y la elección de la foto —un re-render, volver de la cámara— y el
 * archivo se descartaría sin decir nada.
 *
 * Sin `capture`: así iOS ofrece tanto la cámara como el carrete, porque a veces
 * la foto ya está hecha de antes.
 */
export function PhotoButton({
  onFile,
  disabled,
  variant = 'primary',
  children,
  className = '',
  ariaLabel,
}: {
  onFile: (file: File) => void
  disabled?: boolean
  variant?: 'primary' | 'secondary'
  children: ReactNode
  className?: string
  ariaLabel?: string
}) {
  const input = useRef<HTMLInputElement>(null)

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Se limpia para poder volver a elegir exactamente la misma foto.
    event.target.value = ''
    if (file) onFile(file)
  }

  return (
    <>
      <Button
        variant={variant}
        disabled={disabled}
        className={className}
        onClick={() => input.current?.click()}
      >
        {children}
      </Button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        aria-label={ariaLabel ?? 'Elegir foto'}
        onChange={onChange}
      />
    </>
  )
}
